import feathers, { Application, HookContext } from '@feathersjs/feathers'
import Errors from '@feathersjs/errors'
import rest from '@feathersjs/rest-client'
import {
  accessTokenHeader,
  jwtBearHeader,
  timeZoneOffsetHeader
} from './user.session.service'
import {
  CreditApplicationError,
  isApplicationError,
  isExpiredTokenError,
  isLoginError
} from './error.service'

interface initProps {
  fetch: unknown
  root: string
}

const init = ({ fetch, root }: initProps): Application => {
  const app = feathers()
  app.configure(rest(root).fetch(fetch))

  return setup(app)
}

export const errorHandler = (context: HookContext): HookContext | undefined => {
  if (context.error) {
    const error = context.error
    if (isLoginError(error.error || error.message)) {
      context.error = new Errors.BadRequest('Login Error')
      return context
    }
    if (isExpiredTokenError(error.error || error.message)) {
      context.error = new Errors.NotAuthenticated('Not Authenticated')
      return context
    }
    if (isApplicationError(error.error || error.message)) {
      context.error = new CreditApplicationError(error.error || error.message)
      return context
    }
    if (!error.code) {
      context.error = new Errors.GeneralError('Server error')
      return context
    }
  }
}

const setup = (app: Application): Application => {
  // configure header for all services
  app.hooks({
    before: {
      all: [
        (context: HookContext) => {
          return setHeaderTokens(jwtBearHeader())(context)
        }
      ]
    }
  })

  app.service('auth/credentials/confirmation').hooks({
    before: {
      all: [
        (context: HookContext) => {
          return setHeaderTokens({
            ...context.params.headers,
            ...accessTokenHeader()
          })(context)
        }
      ]
    }
  })

  app.service('user').hooks({
    before: {
      create: [
        (context: HookContext) =>
          setHeaderTokens({
            ...context.params.headers,
            ...timeZoneOffsetHeader()
          })(context)
      ]
    }
  })

  app.service('auth/credentials').hooks({
    before: {
      patch: [
        (context: HookContext) => {
          return setHeaderTokens({
            ...context.params.headers,
            ...accessTokenHeader()
          })(context)
        }
      ]
    }
  })

  app.service('accessCodes').hooks({
    before: {
      get: [
        (context: HookContext) =>
          setHeaderTokens({
            ...context.params.headers,
            ...timeZoneOffsetHeader()
          })(context)
      ]
    }
  })

  return app
}

const setHeaderTokens =
  (headers: Record<string, string>) =>
  (context: HookContext): HookContext => {
    context.params.headers = {
      ...context.params.headers,
      ...headers
    }
    return context
  }

export { init }
